import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import {
  externalMonitoringHistoryQuery,
  externalMonitoringPageQuery,
  externalMonitoringStatusQuery,
  type ExternalHistoryRow,
  type ExternalMonitoringTab,
  type ExternalServiceStatus,
} from '../api/devices'
import { DeviceWatch } from './DeviceWatch'
import { LENGTHS, pageButtons } from '../ui/DataTable'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'

/**
 * External Monitoring - externalMonitoring.php: Status, What we watch, History.
 * Filele dupa drepturi. What we watch e `DeviceWatch` (snmpSettings), acelasi
 * ecran care raspunde si la fila Watch din meniul de lucru.
 */

const TABLE = 'snmp-events'

export function DeviceExternalMonitoring({ id }: ScreenProps) {
  const page = useQuery(externalMonitoringPageQuery(id))
  const [active, setActive] = useState<string | null>(null)

  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />
  if (page.data.tabs.length === 0) {
    return (
      <div className="alert alert-danger" role="alert">
        You do not have access to this action.
      </div>
    )
  }

  const current = active && page.data.tabs.some((t) => t.slug === active) ? active : page.data.tabs[0]!.slug

  return (
    <div className="em-wrap">
      <ul className="nav nav-tabs em-tabs" role="tablist">
        {page.data.tabs.map((tab) => (
          <li key={tab.slug} role="presentation" className={tab.slug === current ? 'active' : ''}>
            <a
              href={`#em-${tab.slug}`}
              aria-controls={`em-${tab.slug}`}
              role="tab"
              onClick={(e) => {
                e.preventDefault()
                setActive(tab.slug)
              }}
            >
              <i className={`fas ${tab.icon}`}></i>
              {tab.label}
            </a>
          </li>
        ))}
      </ul>
      <div className="tab-content em-panes">
        {page.data.tabs.map((tab) => (
          <div
            key={tab.slug}
            role="tabpanel"
            className={`tab-pane${tab.slug === current ? ' active' : ''}`}
            id={`em-${tab.slug}`}
          >
            {tab.slug === current ? <Pane id={id} tab={tab} /> : <div className="em-loading"><i className="fas fa-circle-notch fa-spin"></i> Loading...</div>}
          </div>
        ))}
      </div>
      <style>{`
    .em-wrap { padding: 0; }
    .em-tabs { margin-bottom: 0; }
    .em-tabs > li > a { font-size: 13px; padding: 8px 14px; }
    .em-tabs > li > a i { margin-right: 5px; opacity: .7; }
    .em-panes {
        border: 1px solid #e8ecef;
        border-top: 0;
        border-radius: 0 0 3px 3px;
        padding: 14px;
        background: #fff;
    }
    .em-loading { padding: 22px 4px; color: #8b95a1; font-size: 13px; }
    .em-probe { margin: 0 0 12px; font-size: 13px; }
`}</style>
    </div>
  )
}

function Pane({ id, tab }: { id: number; tab: ExternalMonitoringTab }) {
  if (tab.slug === 'status') return <StatusPane id={id} />
  if (tab.slug === 'history') return <HistoryPane id={id} />
  return <DeviceWatch id={id} from="proactivitee" overview={[]} screen={() => null} open={() => undefined} />
}

/**
 * Starea serviciilor urmarite din afara. Exportata fiindca acelasi tabel e
 * fila "Services details" din randul desfacut al unei gazde SNMP
 * (hosts/tabList.php -> proactivitee/snmpServicesStatus/{device}).
 */
export function StatusPane({ id }: { id: number }) {
  const q = useQuery(externalMonitoringStatusQuery(id))
  if (q.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {q.error.message}
      </div>
    )
  }
  if (!q.data) {
    return (
      <div className="em-loading">
        <i className="fas fa-circle-notch fa-spin"></i> Loading...
      </div>
    )
  }
  if (q.data.services.length === 0) {
    return (
      <div className="alert alert-info" role="alert">
        <p style={{ marginBottom: 0 }}>
          Nothing is watched from outside on this device yet.
          Open <strong>What we watch</strong> to say what should be checked.
        </p>
      </div>
    )
  }

  return (
    <>
      {(q.data.probe || q.data.watchedIp) && (
        <p className="text-muted em-probe">
          <i className="fa fa-eye"></i> Watched from <strong>{q.data.probe || 'an unnamed probe'}</strong>
          {q.data.watchedIp ? (
            <>
              {' '}
              at <strong>{q.data.watchedIp}</strong>
            </>
          ) : null}
        </p>
      )}
      <div className="row">
        <div className="col-md-12">
          <table className="table table-striped">
            <thead>
              <tr>
                <th>Service</th>
                <th className="text-center">Status</th>
                <th className="text-center">Last Change</th>
                <th className="text-center">Last Outage Date</th>
                <th className="text-center">Last Outage Time</th>
              </tr>
            </thead>
            <tbody>
              {q.data.services.map((svc) => (
                <StatusRow key={svc.name} svc={svc} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}

function StatusRow({ svc }: { svc: ExternalServiceStatus }) {
  return (
    <tr>
      <td>
        <span>{svc.name}</span>
      </td>
      <td className="text-center">
        <i className={svc.icon} data-toggle="tooltip" data-placement="top" title={svc.title}></i>
      </td>
      <td className="text-center" dangerouslySetInnerHTML={{ __html: svc.changed }} />
      <td className="text-center" dangerouslySetInnerHTML={{ __html: svc.outage_date }} />
      <td className="text-center" dangerouslySetInnerHTML={{ __html: svc.outage_time }} />
    </tr>
  )
}

const HIST_COLS: { key: keyof ExternalHistoryRow; title: string; orderable: boolean; width: number }[] = [
  { key: 'type', title: 'Type', orderable: true, width: 100 },
  { key: 'details', title: 'Service Details', orderable: true, width: 100 },
  { key: 'info', title: 'Additional Info', orderable: true, width: 100 },
  { key: 'outage', title: 'Outage Date', orderable: true, width: 100 },
  { key: 'up', title: 'Service UP', orderable: true, width: 100 },
  { key: 'recover', title: 'Recover Date', orderable: true, width: 100 },
  { key: 'duration', title: 'Outage Duration', orderable: true, width: 100 },
]

function HistoryPane({ id }: { id: number }) {
  const [start, setStart] = useState(0)
  const [length, setLength] = useState(10)
  const [order, setOrder] = useState<{ column: number; dir: 'asc' | 'desc' }>({ column: 3, dir: 'desc' })
  const table = useQuery({
    ...externalMonitoringHistoryQuery(id, {
      start,
      length,
      orderColumn: order.column,
      orderDir: order.dir,
      search: '',
    }),
    placeholderData: keepPreviousData,
  })

  if (table.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {table.error.message}
      </div>
    )
  }

  const rows = table.data?.data ?? []
  const total = table.data?.recordsFiltered ?? 0
  const pages = Math.max(1, Math.ceil(total / length) || 1)
  const page = Math.floor(start / length)
  const from = total === 0 ? 0 : start + 1
  const to = Math.min(start + length, total)

  return (
    <div className="row">
      <div className="col-md-12">
        <div className="dataTables_wrapper form-inline dt-bootstrap no-footer" id={`${TABLE}_wrapper`}>
          <div className="row">
            <div className="col-sm-12">
              <div className="dataTables_processing" id={`${TABLE}_processing`} style={{ display: table.isFetching ? 'block' : 'none' }}>
                Processing...
              </div>
              <table className="table table-striped table-hover dataTable snmp-events no-footer" width="100%" id={TABLE} role="grid">
                <thead>
                  <tr role="row">
                    {HIST_COLS.map((col, i) => {
                      const sorted = order.column === i
                      const cls = [
                        'td-small a-left',
                        sorted ? (order.dir === 'asc' ? 'sorting_asc' : 'sorting_desc') : 'sorting',
                      ].join(' ')
                      return (
                        <th
                          key={col.key}
                          className={cls}
                          style={{ width: col.width }}
                          onClick={() => {
                            setOrder({ column: i, dir: sorted && order.dir === 'desc' ? 'asc' : 'desc' })
                            setStart(0)
                          }}
                        >
                          {col.title}
                        </th>
                      )
                    })}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr className="odd">
                      <td colSpan={7} className="dataTables_empty">
                        No data available in table
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, i) => (
                      <tr key={row.DT_RowId} id={row.DT_RowId} role="row" className={i % 2 === 0 ? 'odd' : 'even'}>
                        {HIST_COLS.map((col) => (
                          <td key={col.key} className="td-small a-left">
                            {row[col.key]}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className="row">
            <div className="col-sm-2">
              <div className="dataTables_length custom_length_class" id={`${TABLE}_length`}>
                <label>
                  Show{' '}
                  <select
                    name={`${TABLE}_length`}
                    className="form-control input-sm"
                    value={length}
                    onChange={(e) => {
                      setLength(Number(e.target.value))
                      setStart(0)
                    }}
                  >
                    {LENGTHS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>{' '}
                  entries
                </label>
              </div>
            </div>
            <div className="col-sm-4 aright">
              <div className="dataTables_info" id={`${TABLE}_info`}>
                Showing {from} to {to} of {total} entries
              </div>
            </div>
            <div className="col-sm-6">
              <div className="dataTables_paginate paging_simple_numbers" id={`${TABLE}_paginate`}>
                <ul className="pagination">
                  <li className={`paginate_button previous${page <= 0 ? ' disabled' : ''}`}>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        if (page > 0) setStart((page - 1) * length)
                      }}
                    >
                      Previous
                    </a>
                  </li>
                  {pageButtons(page, pages).map((b, i) =>
                    b === 'ellipsis' ? (
                      <li key={`e${i}`} className="paginate_button disabled">
                        <a href="#" onClick={(e) => e.preventDefault()}>
                          …
                        </a>
                      </li>
                    ) : (
                      <li key={b} className={`paginate_button${b === page ? ' active' : ''}`}>
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault()
                            setStart(b * length)
                          }}
                        >
                          {b + 1}
                        </a>
                      </li>
                    ),
                  )}
                  <li className={`paginate_button next${page >= pages - 1 ? ' disabled' : ''}`}>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        if (page < pages - 1) setStart((page + 1) * length)
                      }}
                    >
                      Next
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="clear"></div>
        </div>
      </div>
    </div>
  )
}
